import { memo, useEffect, useMemo } from 'react';

import { Divider, Grid } from '@explorer/core';
import { Form, FormFieldControl, SubmitButton } from '@explorer/forms';
import { ClientRoles } from '@explorer/forms/types';
import { ControlClientInfoResponse } from '@explorer/open-api-client';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';

import { DeletePrepurchaseDialog } from './DeletePrePurchaseDialog';
import {
  FormValues,
  getInitialValues,
  getModules,
  initializeFormSchema,
  validationSchema,
} from './formSchema';
import { DisplayModules } from './Modules/Modules';
import { useModifyClientSubmit } from './useModifyClientSubmit';

export const ModifyClient = memo(function ModifyClient({
  activeClient,
}: ModifyClientProps) {
  const { client_id: clientId } = activeClient?.client ?? {};
  const { formatMessage } = useIntl();
  const formId = `ModifyClient-${clientId}`;

  const modules = useMemo(
    () => getModules(activeClient?.client),
    [activeClient?.client],
  );

  const { onSubmit, open, setOpen } = useModifyClientSubmit({
    activeClient,
    clientId,
    modules,
  });

  const formik = useFormik<FormValues>({
    validationSchema,
    initialValues: getInitialValues(activeClient, modules),
    onSubmit: (...args) => onSubmit(...args, false),
  });

  const formSchema = useMemo(
    () =>
      initializeFormSchema({
        formId,
        formik,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik],
  );

  // If the role is changed to premium, set count_max_extended to 1
  // If the role is changed to freemium, set count_max_extended to 0
  useEffect(() => {
    if (
      (formik?.values?.role === ClientRoles.PORTAL_PREMIUM ||
        formik?.values?.role === ClientRoles.PORTAL_POLICYHOLDER) &&
      formik.values.count_max_extended < 1
    ) {
      formik.setFieldValue('count_max_extended', 1);
    } else if (
      formik?.values?.role === ClientRoles.PORTAL_FREEMIUM &&
      formik.values.count_max_extended > 0
    ) {
      formik.setFieldValue('count_max_extended', 0);
    }
    // disabling this b/c we only want to set count_max_extended to 1 when the role is changed to premium
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values?.role]);

  /**
   * Disable the submit button
   */
  const disableSave = useMemo<boolean>(() => {
    const { isSubmitting, isValid } = formik;

    if (isSubmitting || !isValid) return true;

    return false;
  }, [formik]);

  return (
    <>
      <DeletePrepurchaseDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => {
          setOpen(false);
          onSubmit(formik.values, formik, true);
        }}
      />
      <Form formId={formId} formik={formik}>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
          <Grid item xs={6}>
            <FormFieldControl {...formSchema.count_max_lite} />
          </Grid>
          <Grid item xs={6}>
            <FormFieldControl {...formSchema.count_max_extended} />
          </Grid>
          <Grid item xs={12}>
            <FormFieldControl {...formSchema.role} />
          </Grid>
          <Grid item xs={12}>
            <Divider mb={2}>
              {formatMessage({ id: 'forms-presets.modules' })}
            </Divider>
          </Grid>
          <DisplayModules
            activeClient={activeClient}
            moduleInfo={modules}
            formik={formik}
            formId={formId}
            schema={formSchema}
            triggerSave={() => {
              formik.submitForm();
            }}
          />
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            <SubmitButton
              formId={formId}
              formik={formik}
              color="primary"
              label={{ id: 'forms-presets.save' }}
              disabled={disableSave}
            />
          </Grid>
        </Grid>
      </Form>
    </>
  );
});

export interface ModifyClientProps {
  readonly activeClient?: ControlClientInfoResponse;
}
